import { FC } from 'react';

import { LoadingOutlined, MailOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { T } from 'react-translator-component';

import * as S from './styled';
import { useTwoFactor } from './useTwoFactor';

interface TwoFactorVerificationProps {
  email: string;
  maskedEmail: string;
  onSuccess: (user: any) => void;
  onBackToLogin: () => void;
}

const TwoFactorVerification: FC<TwoFactorVerificationProps> = ({
  email,
  maskedEmail,
  onSuccess,
  onBackToLogin,
}) => {
  const {
    digits,
    inputRefs,
    isLoading,
    errorMessage,
    expiryCountdown,
    resendCountdown,
    attemptsRemaining,
    isBlocked,
    handleDigitChange,
    handleKeyDown,
    handlePaste,
    handleResend,
    formatTime,
  } = useTwoFactor({ email, maskedEmail, onSuccess, onBackToLogin });

  return (
    <S.TwoFactorWrapper>
      <S.TwoFactorIcon>
        <MailOutlined />
      </S.TwoFactorIcon>

      <S.TwoFactorTitle>{T('Verification Code')}</S.TwoFactorTitle>

      <S.TwoFactorSubtitle>
        {T('We sent a code to')} <strong>{maskedEmail}</strong>
      </S.TwoFactorSubtitle>

      <S.CodeInputContainer onPaste={handlePaste}>
        {digits.map((digit, index) => (
          <S.CodeInput
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleDigitChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            disabled={isLoading || isBlocked}
            autoFocus={index === 0}
          />
        ))}
      </S.CodeInputContainer>

      {isLoading && (
        <Spin
          indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          style={{ marginBottom: 12 }}
        />
      )}

      {errorMessage && <S.ErrorText>{errorMessage}</S.ErrorText>}

      {!isBlocked && (
        <>
          <S.CountdownText>
            {expiryCountdown > 0
              ? `${T('Code expires in')} ${formatTime(expiryCountdown)}`
              : T('Code expired. Request a new one.')}
          </S.CountdownText>

          <S.CountdownText>
            {attemptsRemaining < 5 &&
              `${attemptsRemaining} ${T('attempts remaining')}`}
          </S.CountdownText>

          <S.ResendButton
            onClick={handleResend}
            disabled={resendCountdown > 0 || isLoading}
          >
            {resendCountdown > 0
              ? `${T('Resend code in')} ${formatTime(resendCountdown)}`
              : T('Resend code')}
          </S.ResendButton>
        </>
      )}

      <S.BackToLoginLink onClick={onBackToLogin}>
        {T('Back to login')}
      </S.BackToLoginLink>
    </S.TwoFactorWrapper>
  );
};

export default TwoFactorVerification;
